import { Link } from 'react-router-dom';
import { usePortalAccess } from './PortalAccessContext';
import { PortalTopbar } from './PortalTopbar';
import './dashboard.css';

interface ModuleCardProps {
  to?: string;
  image?: string;
  imagePosition?: string;
  imageZoom?: number;
  title: string;
  description: string;
  featured?: boolean;
  enabled: boolean;
}

function ModuleCard({
  to,
  image,
  imagePosition,
  imageZoom,
  title,
  description,
  featured,
  enabled,
}: ModuleCardProps): JSX.Element {
  const className = [
    'db-card',
    featured ? 'db-card-featured' : '',
    enabled ? '' : 'db-card-disabled',
    image ? '' : 'db-card-no-image',
  ]
    .filter(Boolean)
    .join(' ');

  const imageStyle = {
    ...(imagePosition ? { objectPosition: imagePosition } : null),
    ...(imageZoom ? { transform: `scale(${imageZoom})` } : null),
  };

  const content = (
    <>
      {image && <img src={image} alt="" style={imageStyle} />}
      <span className="db-card-info">
        <span className="db-card-title">{title}</span>
        <span className="db-card-desc">{description}</span>
        {!enabled && <span className="db-card-badge">Próximamente</span>}
      </span>
    </>
  );

  if (!enabled || !to) {
    return (
      <div className={className} aria-disabled="true">
        {content}
      </div>
    );
  }

  return (
    <Link to={to} className={className}>
      {content}
    </Link>
  );
}

/**
 * Selector de módulos del portal. Estilo y disposición (héroe + grilla de
 * tarjetas con foto) replican, a pedido, el panel de
 * `01-sintax-proposals/bahia-blanca/index.html` — reutiliza sus imágenes
 * como placeholder hasta tener material propio.
 */
export function ClientPortalDashboard(): JSX.Element {
  const { config } = usePortalAccess();
  const modules = config?.modules;

  return (
    <div className="db-page">
      <PortalTopbar />

      <div className="db-hero">
        <img src="/assets/dashboard/hero-bahia-blanca.png" alt="Vista aérea de Bahía Blanca" />
        <div className="db-hero-overlay" />
        <div className="db-hero-content">
          <h1 className="db-hero-title">{config?.title ?? 'Bahía Blanca'}</h1>
          <p className="db-hero-subtitle">
            Herramientas, análisis y documentación del proyecto de transporte público.
          </p>
        </div>
      </div>

      <div className="db-wrap">
        <div className="db-section-label">Ecosistema de trabajo</div>
        <div className="db-cards-grid">
          <ModuleCard
            featured
            to="/analysis"
            image="/assets/dashboard/card-red-programacion.png"
            imagePosition="center 20%"
            imageZoom={1.18}
            title="Sistema de Transporte"
            description="Dashboard general, líneas, mapa del sistema y análisis técnico de la red."
            enabled={modules?.analysis.enabled ?? false}
          />
          <ModuleCard
            to="/documentation"
            image="/assets/dashboard/card-pliego.png"
            title="Documentación"
            description="Pliegos, anexos e informes del proyecto."
            enabled={modules?.documentation.enabled ?? false}
          />
          <ModuleCard
            title="Transit Suite"
            description="Acceso al módulo de operación de la red."
            enabled={false}
          />
        </div>
      </div>
    </div>
  );
}
